import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense } from 'react';

// Layouts
import DashboardLayout from '@/layouts/DashboardLayout';
import AuthLayout from '@/layouts/AuthLayout';

// Auth Pages
import Login from '@/pages/auth/Login';
import ForgotPassword from '@/pages/auth/ForgotPassword';

// ============================================
// ADVANCED PAGES - NEW PROFESSIONAL UI/UX
// ============================================

// Advanced Dashboard
const DashboardAdvanced = lazy(() => import('@/pages/dashboard/DashboardAdvanced'));

// Real-Time Monitor
const RealTimeMonitor = lazy(() => import('@/pages/dashboard/RealTimeMonitor'));

// Advanced User Management
const UsersAdvanced = lazy(() => import('@/pages/users/UsersAdvanced'));

// Advanced KYC
const KYCAdvanced = lazy(() => import('@/pages/kyc/KYCAdvanced'));

// Advanced Financial
const TransactionsAdvanced = lazy(() => import('@/pages/transactions/TransactionsAdvanced'));
// Advanced Games
const GamesAdvanced = lazy(() => import('@/pages/games/GamesAdvanced'));

// Advanced Settings
const SettingsAdvanced = lazy(() => import('@/pages/settings/SettingsAdvanced'));

// Advanced Admin Users
const AdminUsersAdvanced = lazy(() => import('@/pages/settings/AdminUsersAdvanced'));

// Settings Pages
const SecuritySettings = lazy(() => import('@/pages/settings/SecuritySettings'));
const SystemHealth = lazy(() => import('@/pages/settings/SystemHealth'));

// Advanced Analytics
const AnalyticsAdvanced = lazy(() => import('@/pages/reports/AnalyticsAdvanced'));

// Report Pages
const RevenueReports = lazy(() => import('@/pages/reports/RevenueReports'));
const UsersReports = lazy(() => import('@/pages/reports/UsersReports'));
const GamesReports = lazy(() => import('@/pages/reports/GamesReports'));
const FinancialReports = lazy(() => import('@/pages/reports/FinancialReports'));
const CustomReports = lazy(() => import('@/pages/reports/CustomReports'));

// Advanced Marketing
const ReferralsAdvanced = lazy(() => import('@/pages/marketing/ReferralsAdvanced'));
const NotificationsAdvanced = lazy(() => import('@/pages/marketing/NotificationsAdvanced'));
const NotificationsSend = lazy(() => import('@/pages/marketing/NotificationsSend'));
const NotificationTemplates = lazy(() => import('@/pages/marketing/NotificationTemplates'));

// Advanced Support
const SupportAdvanced = lazy(() => import('@/pages/support/SupportAdvanced'));

// ============================================
// LEGACY PAGES (only existing files)
// ============================================

// Dashboard Pages (Legacy - existing)
import Dashboard from '@/pages/dashboard/Dashboard';

// User Management Pages (Legacy - existing)
import Users from '@/pages/users/Users';
import BannedUsers from '@/pages/users/BannedUsers';
import UserActivity from '@/pages/users/UserActivity';

// KYC Pages (Legacy - existing)
import KYCList from '@/pages/kyc/KYCList';
import KYCSettings from '@/pages/kyc/KYCSettings';

// Financial Pages (Legacy - existing)
import Transactions from '@/pages/financial/Transactions';
import WalletOverview from '@/pages/financial/WalletOverview';
import PaymentSettings from '@/pages/financial/PaymentSettings';
import QRManagement from '@/pages/financial/QRManagement';
import Deposits from '@/pages/financial/Deposits';
import Withdrawals from '@/pages/financial/Withdrawals';

// Game Management Pages (Legacy - existing)
import LudoRooms from '@/pages/games/LudoRooms';
import GameDisputes from '@/pages/games/GameDisputes';
import GameSettings from '@/pages/games/GameSettings';

// Settings Pages (Legacy - existing)
import GeneralSettings from '@/pages/settings/GeneralSettings';

// Other Pages
import Leaderboard from '@/pages/Leaderboard';
import Promotions from '@/pages/marketing/Promotions';
import BonusManagement from '@/pages/marketing/BonusManagement';
import FAQManagement from '@/pages/support/FAQManagement';

// Loading component for lazy loaded pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AnimatePresence mode="wait">
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* Protected Dashboard Routes */}
        <Route
          element={
            isAuthenticated ? <DashboardLayout /> : <Navigate to="/login" replace />
          }
        >
          {/* ============================================ */}
          {/* ADVANCED ROUTES - NEW PROFESSIONAL UI/UX    */}
          {/* ============================================ */}

          {/* Advanced Dashboard (Default) */}
          <Route index element={
            <Suspense fallback={<PageLoader />}>
              <DashboardAdvanced />
            </Suspense>
          } />

          {/* Real-Time Monitor */}
          <Route path="real-time" element={
            <Suspense fallback={<PageLoader />}>
              <RealTimeMonitor />
            </Suspense>
          } />

          {/* Advanced User Management */}
          <Route path="users" element={
            <Suspense fallback={<PageLoader />}>
              <UsersAdvanced />
            </Suspense>
          } />

          {/* Banned Users */}
          <Route path="users/banned" element={<BannedUsers />} />

          {/* User Activity */}
          <Route path="users/activity" element={<UserActivity />} />

          {/* Advanced KYC */}
          <Route path="kyc" element={
            <Suspense fallback={<PageLoader />}>
              <KYCAdvanced />
            </Suspense>
          } />

          {/* KYC Settings */}
          <Route path="kyc/settings" element={<KYCSettings />} />

          {/* Advanced Financial */}
          <Route path="transactions" element={
            <Suspense fallback={<PageLoader />}>
              <TransactionsAdvanced />
            </Suspense>
          } />
          <Route path="deposits" element={<Deposits />} />
          <Route path="withdrawals" element={<Withdrawals />} />

          {/* Wallet Overview */}
          <Route path="wallet" element={<WalletOverview />} />

          {/* Payment Settings */}
          <Route path="payments/settings" element={<PaymentSettings />} />

          {/* QR Management (for manual deposits) */}
          <Route path="qr-management" element={<QRManagement />} />

          {/* Advanced Games */}
          <Route path="games" element={
            <Suspense fallback={<PageLoader />}>
              <GamesAdvanced />
            </Suspense>
          } />

          {/* Game Disputes */}
          <Route path="games/disputes" element={<GameDisputes />} />

          {/* Game Settings */}
          <Route path="games/settings" element={<GameSettings />} />

          {/* Leaderboard */}
          <Route path="leaderboard" element={<Leaderboard />} />

          {/* Promotions */}
          <Route path="promotions" element={<Promotions />} />

          {/* Bonus Management */}
          <Route path="bonus" element={<BonusManagement />} />

          {/* FAQ Management */}
          <Route path="support/faq" element={<FAQManagement />} />

          {/* Advanced Settings */}
          <Route path="settings" element={
            <Suspense fallback={<PageLoader />}>
              <SettingsAdvanced />
            </Suspense>
          } />

          {/* Advanced Admin Users */}
          <Route path="settings/admins" element={
            <Suspense fallback={<PageLoader />}>
              <AdminUsersAdvanced />
            </Suspense>
          } />

          {/* Settings - Security */}
          <Route path="settings/security" element={
            <Suspense fallback={<PageLoader />}>
              <SecuritySettings />
            </Suspense>
          } />

          {/* Settings - System Health */}
          <Route path="settings/health" element={
            <Suspense fallback={<PageLoader />}>
              <SystemHealth />
            </Suspense>
          } />

          {/* Advanced Analytics */}
          <Route path="analytics" element={
            <Suspense fallback={<PageLoader />}>
              <AnalyticsAdvanced />
            </Suspense>
          } />

          {/* Report Pages */}
          <Route path="reports/revenue" element={
            <Suspense fallback={<PageLoader />}>
              <RevenueReports />
            </Suspense>
          } />
          <Route path="reports/users" element={
            <Suspense fallback={<PageLoader />}>
              <UsersReports />
            </Suspense>
          } />
          <Route path="reports/games" element={
            <Suspense fallback={<PageLoader />}>
              <GamesReports />
            </Suspense>
          } />
          <Route path="reports/financial" element={
            <Suspense fallback={<PageLoader />}>
              <FinancialReports />
            </Suspense>
          } />
          <Route path="reports/custom" element={
            <Suspense fallback={<PageLoader />}>
              <CustomReports />
            </Suspense>
          } />

          {/* Advanced Support */}
          <Route path="support" element={
            <Suspense fallback={<PageLoader />}>
              <SupportAdvanced />
            </Suspense>
          } />

          {/* Advanced Referrals */}
          <Route path="referrals" element={
            <Suspense fallback={<PageLoader />}>
              <ReferralsAdvanced />
            </Suspense>
          } />

          {/* Advanced Notifications */}
          <Route path="notifications" element={
            <Suspense fallback={<PageLoader />}>
              <NotificationsAdvanced />
            </Suspense>
          } />

          {/* Notifications - Send New */}
          <Route path="notifications/send" element={
            <Suspense fallback={<PageLoader />}>
              <NotificationsSend />
            </Suspense>
          } />

          {/* Notifications - Templates */}
          <Route path="notifications/templates" element={
            <Suspense fallback={<PageLoader />}>
              <NotificationTemplates />
            </Suspense>
          } />

          {/* ============================================ */}
          {/* LEGACY ROUTES - Keeping for compatibility   */}
          {/* ============================================ */}

          {/* Legacy Dashboard */}
          <Route path="dashboard/legacy" element={<Dashboard />} />

          {/* Legacy User Management */}
          <Route path="users/legacy" element={<Users />} />

          {/* Legacy KYC */}
          <Route path="kyc/legacy" element={<KYCList />} />

          {/* Legacy Financial */}
          <Route path="transactions/legacy" element={<Transactions />} />

          {/* Legacy Games */}
          <Route path="games/ludo" element={<LudoRooms />} />

          {/* Legacy Settings */}
          <Route path="settings/legacy" element={<GeneralSettings />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-400 animate-pulse">Loading...</p>
      </div>
    </div>
  );
}

export default App;
